/**
 * Offerte e chat WhatsApp: logica pura (niente React, niente rete).
 *
 * - "Nuova offerta" dal pannello della chat apre la pagina offerte in una nuova
 *   scheda con il Lead/Cliente gia' scelto (`newDealUrl`).
 * - "Invia in chat" manda il PDF del preventivo come documento: qui si decide
 *   se si puo' inviare (`pdfSendBlock`) e la frase di accompagnamento proposta.
 * - "Scrivi su WhatsApp" dalla scheda offerta: numero da usare e testo di
 *   sollecito precompilato (non inviato).
 */

export interface DealLike {
  title?: string | null;
  quote_number?: string | number | null;
}

export interface ClientPhoneLike {
  whatsapp_number?: string | null;
  phone?: string | null;
}

/** Link alla pagina offerte con il modulo di creazione aperto sul Lead/Cliente. */
export function newDealUrl(clientId: string): string {
  return `/deals?create=true&clientId=${encodeURIComponent(clientId)}`;
}

/** Nome dell'offerta come si legge in un messaggio: numero e titolo se ci sono. */
export function dealLabel(deal: DealLike): string {
  const title = (deal.title || '').trim();
  const num = deal.quote_number != null && String(deal.quote_number).trim() !== '' ? String(deal.quote_number).trim() : '';
  if (num && title) return `n. ${num} "${title}"`;
  if (num) return `n. ${num}`;
  if (title) return `"${title}"`;
  return '';
}

/** Frase proposta sotto il PDF inviato in chat (modificabile dall'utente). */
export function dealPdfCaption(deal: DealLike): string {
  const label = dealLabel(deal);
  return `Buongiorno, le invio il preventivo${label ? ` ${label}` : ''}. Resto a disposizione per qualsiasi domanda.`;
}

/** Testo di sollecito precompilato quando si apre la chat dalla scheda offerta. */
export function dealReminderText(deal: DealLike): string {
  const label = dealLabel(deal);
  return `Buongiorno, le scrivo per sapere se ha avuto modo di guardare il preventivo${label ? ` ${label}` : ''}. Se ha domande sono a disposizione.`;
}

/** Numero per la chat: prima quello WhatsApp, poi il telefono. */
export function clientChatPhone(client: ClientPhoneLike | null | undefined): string | null {
  const wa = (client?.whatsapp_number || '').trim();
  if (wa) return wa;
  const phone = (client?.phone || '').trim();
  return phone || null;
}

export type PdfSendBlock =
  | 'other_chat'
  | 'window_checking'
  | 'window_closed'
  | 'no_template'
  | 'caption_too_long';

export interface PdfSendInput {
  /** Chat del pannello laterale. */
  chatId: string;
  /** Chat aperta nel composer (dove l'invio finirebbe davvero). */
  selectedChatId: string | null;
  /** Numero Ufficiale (Cloud): vale la finestra di 24 ore. */
  isCloud: boolean;
  cswUnknown: boolean;
  canSendFreeForm: boolean;
  templateId: string | null | undefined;
  caption: string;
}

/** Limite Meta per la didascalia di un documento. */
export const CAPTION_MAX = 1024;

/** Motivo per cui l'invio non si puo' fare, oppure null se si puo'. */
export function pdfSendBlock(i: PdfSendInput): PdfSendBlock | null {
  if (!i.selectedChatId || i.selectedChatId !== i.chatId) return 'other_chat';
  if (i.isCloud && i.cswUnknown) return 'window_checking';
  if (i.isCloud && !i.canSendFreeForm) return 'window_closed';
  if (!i.templateId) return 'no_template';
  if (i.caption.length > CAPTION_MAX) return 'caption_too_long';
  return null;
}

export const PDF_SEND_BLOCK_TEXT: Record<PdfSendBlock, string> = {
  other_chat: 'La chat aperta non è questa: riapri la conversazione e riprova.',
  window_checking: 'Sto controllando la finestra di 24 ore di WhatsApp...',
  window_closed: 'Finestra di 24 ore chiusa: con il numero Ufficiale puoi inviare solo template approvati da Meta finché il cliente non riscrive. Il PDF non viene inviato.',
  no_template: 'Nessun template adatto ai servizi di questa offerta: controlla i template nelle Impostazioni preventivi.',
  caption_too_long: `Il messaggio è troppo lungo (massimo ${CAPTION_MAX} caratteri).`,
};
